import { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Grid,
  Alert,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  CircularProgress,
} from "@mui/material";
import api from '../config/api';
import { useTranslation } from 'react-i18next';

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: "80%", md: "60%", lg: "50%" },
  maxWidth: 600,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  maxHeight: "90vh",
  overflowY: "auto",
};

const OrganizationUserModal = ({
  openModal,
  handleClose,
  type,
  selectedUser,
  authToken,
  organizationId,
  showAlert,
  setCounter,
  currentUserRole,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    rol: "user",
  });

  const { t } = useTranslation();

  // Roles disponibles según el usuario actual
  const availableRoles =  ["super-admin","admin-grafton","admin"].includes(currentUserRole) 
    ? [
        { value: "user", label: t('user') },
        { value: "admin", label: t('admin') },
      ]
    : [{ value: "user", label: t('user') }];

  // Cargar proyectos cuando se abre el modal para crear/editar
  useEffect(() => {
    if (openModal && organizationId && authToken && (type === "add" || type === "edit")) {
      loadOrganizationProjects();
    }
  }, [openModal, organizationId, authToken, type]);

  useEffect(() => {
    if (selectedUser && type === "edit") {
      setForm({
        name: selectedUser.name || "",
        email: selectedUser.email || "",
        password: "",
        rol: selectedUser.rol || "user",
      });
      // Para edición, establecer proyectos actuales del usuario
      setSelectedProjects(selectedUser.projects || []);
    } else {
      setForm({
        name: "",
        email: "",
        password: "",
        rol: "user",
      });
      setSelectedProjects([]);
    }
  }, [selectedUser, type]);

  // Cuando cambia el rol en modo "add", manejar asignación automática de proyectos
  useEffect(() => {
    if (type === "add" && form.rol === "admin" && projects.length > 0) {
      // Si es admin, asignar todos los proyectos automáticamente
      setSelectedProjects(projects.map(p => p.uid));
    } else if (type === "add" && form.rol === "user") {
      // Si es user, limpiar selección para que el admin escoja
      setSelectedProjects([]);
    }
  }, [form.rol, projects, type]);

  const loadOrganizationProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await api.get(
        `/v2/projects/${organizationId}`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (response.data.response?.message === "All projects were successfully obtained") {
        setProjects(response.data.response.data);
      } else {
        throw new Error("Error al cargar proyectos");
      }
    } catch (error) {
      console.error("Error loading projects:", error);
      showAlert(t('users.projectsLoadError'), "error");
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleProjectToggle = (projectId) => {
    setSelectedProjects(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      } else {
        return [...prev, projectId];
      }
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (type === "add") {
        // Crear usuario en la organización específica
        const createResponse = await api.post(
          `/users/register?organizationId=${organizationId}`,
          form,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        // Si se creó exitosamente y hay proyectos seleccionados, asignarlos
        if (createResponse.data && selectedProjects.length > 0) {
          const userId = createResponse.data.response?.data?.uid;
          if (userId) {
            await api.put(
              `/projects/assignProjects/${userId}?organizationId=${organizationId}`,
              { projects: selectedProjects },
              {
                headers: { Authorization: `Bearer ${authToken}` },
              }
            );
          }
        }

        showAlert(t('users.userCreateSuccess'), "success");
      } else if (type === "edit") {
        // Actualizar datos básicos del usuario
        await api.put(
          `/users/${selectedUser.uid}`,
          { 
            name: form.name, 
            rol: form.rol 
          },
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        // Actualizar proyectos asignados
        await api.put(
          `/projects/assignProjects/${selectedUser.uid}?organizationId=${organizationId}`,
          { projects: selectedProjects },
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        showAlert(t('users.userEditSuccess'), "success");
      } else if (type === "delete") {
        // Eliminar usuario
        await api.delete(
          `/users/${selectedUser.uid}?organizationId=${organizationId}`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        showAlert(t('users.userDeleteSuccess'), "success");
      }

      handleClose();
      setCounter?.((prev) => prev + 1);
    } catch (err) {
      console.error(err);
      
      // Manejo específico de errores de límite
      if (err.response?.status === 409) {
        const errorData = err.response.data.response;
        showAlert(`${t('users.usageLimitReached')}: ${errorData.currentUsage}/${errorData.limit}`, "error");
      } else {
        showAlert(t('generalApiError'), "error");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={openModal} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Typography variant="h6" sx={{ textAlign: "center", mb: 3 }}>
          {type === "add"
            ? t('users.userCreate')
            : type === "edit"
            ? t('users.userEdit')
            : t('users.userDelete')}
        </Typography>

        {type === "delete" ? (
          <>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography align="center" sx={{ fontSize: "1.1rem" }}>
                {t('users.userDeleteConfirm')}{" "}
                <strong>{selectedUser?.name}</strong>?
              </Typography>
            </Alert>
            <Typography variant="body2" color="error" sx={{ textAlign: "center", mb: 3 }}>
              {t('noWayBack')}
            </Typography>
          </>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t('name')}
                  name="name"
                  fullWidth
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </Grid>

              {type === "add" && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      label={t('email')}
                      name="email"
                      type="email"
                      fullWidth
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t('password')}
                      name="password"
                      type="password"
                      fullWidth
                      value={form.password}
                      onChange={handleChange}
                      required
                      helperText="Mínimo 6 caracteres"
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <TextField
                  select
                  label={t('role')}
                  name="rol"
                  fullWidth
                  value={form.rol}
                  onChange={handleChange}
                  required
                >
                  {availableRoles.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Sección de asignación de proyectos */}
              {(type === "add" || type === "edit") && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      {t('users.assignProjects')}
                    </Typography>
                    
                    {form.rol === "admin" ? (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          Los usuarios administradores tienen acceso automático a todos los proyectos de la organización.
                        </Typography>
                      </Alert>
                    ) : (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('users.selectProjects')}:
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    {loadingProjects ? (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : projects.length === 0 ? (
                      <Alert severity="warning">
                        <Typography variant="body2">
                          {t('users.noProjectsInOrganization')}
                        </Typography>
                      </Alert>
                    ) : (
                      <FormGroup>
                        {projects.map((project) => (
                          <FormControlLabel
                            key={project.uid}
                            control={
                              <Checkbox
                                checked={selectedProjects.includes(project.uid)}
                                onChange={() => handleProjectToggle(project.uid)}
                                disabled={loading || form.rol === "admin"}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {project.name}
                                </Typography>
                                {project.description && (
                                  <Typography variant="caption" color="text.secondary">
                                    {project.description}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        ))}
                      </FormGroup>
                    )}

                    {form.rol !== "admin" && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>{t('users.selectedProjects')}:</strong> {selectedProjects.length}
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </>
              )}


            </Grid>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
          <Button onClick={handleClose} disabled={loading}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            color={type === "delete" ? "error" : "primary"}
          >
            {loading
              ? t('processing')
              : type === "add"
              ? t('create')
              : type === "edit"
              ? t('save')
              : t('delete')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default OrganizationUserModal;